import {inject} from 'vue';
import type {PrismicDocument} from '@prismicio/client';
import {callWithNuxt} from '#app';
import {useLogger, useRoute, useSpanWrap, useNuxtApp, navigateTo, usePrismicLang, useLocalePrefix} from '#imports';
import {FETCH_LINKS, PRISMIC_CLIENT} from '~/app/keys';
import usePrismicRedirect from '~~/modules/redirect/usePrismicRedirect';

/**
 * Retrieve document from Prismic repository.
 * @param documentType  Document type.
 * @param uid           Document UID.
 */

export default async function usePrismicDocument<T extends PrismicDocument>(documentType: string, uid?: string): Promise<T | undefined | null> {
    const logger = useLogger();
    const client = inject(PRISMIC_CLIENT);
    const nuxtApp = useNuxtApp();
    const route = useRoute();
    const {lang} = usePrismicLang();
    const {withLocalePrefix} = useLocalePrefix();

    try {
        const getPrismicDocument = () =>
            uid
                ? client!.getByUID<T>(documentType, uid, {lang: lang.value, fetchLinks: FETCH_LINKS})
                : client!.getSingle<T>(documentType, {lang: lang.value, fetchLinks: FETCH_LINKS});
        return await useSpanWrap(getPrismicDocument, {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${documentType}`});
    } catch (_) {
        logger.info(`Could not retrieve document from Prismic with parameters: [${documentType}, ${lang.value}, ${uid}]. Trying redirection...`);
    }

    // if the document does not exist, check for redirect or throw 404
    const redirect = await callWithNuxt(nuxtApp, usePrismicRedirect, [route.path, lang.value, client]);
    if (!redirect) {
        return;
    }
    callWithNuxt(nuxtApp, () => navigateTo(withLocalePrefix(redirect.data.redirectTo as string), {redirectCode: 301}));
}
